// היום שלי — the landing page: everything today holds, in one place.
//
// IT GATHERS, IT DOES NOT COLLECT. Three of its four sections come from ONE payload that
// was already fetched — `GET /api/alerts`, held by AlertsContext for the bell and the
// sidebar dots — so the dated tasks and the lead follow-ups on this page are literally
// the rows the bell is showing above it. A second collector would have been a second
// opinion about what has come due, and the two would eventually have disagreed on one
// screen. Nothing on the server was added or changed for this page.
//
// WHAT IT ADDS IS THE DIARY. The bell carries no meetings on purpose — a meeting cannot
// be late, so it is not an alert — but a day page is not an alert list, it is a plan, and
// where I have to BE is most of it. That is this page's own fetch: the calendar's window
// endpoint asked for a single day.
//
// DISPLAY ONLY. It creates nothing, edits nothing and writes nothing; every row is a link
// to where that thing actually lives. No calculation reads it and it reads no money
// beyond a count the notification payload already carried.
//
// TWO "TODAY"S MEET HERE and the difference is not this page's to fix: tasks and
// follow-ups were judged against the server's `today()` (UTC), while the meetings window
// is asked for with the browser's local date. Between midnight and the small hours in
// Israel they differ by a day. That is already true of the bell beside the calendar, and
// it belongs to whoever owns calculations/dueState.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { authApi } from '../../api';
import { meetingsApi } from '../../api/calendar';
import { useAuth } from '../../context/AuthContext';
import { useAlerts } from '../../context/AlertsContext';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import DateText from '../../components/DateText';
import { involvesPartner } from '../Calendar/meetingKind';
import { todayStr } from '../../utils/dates';
import DaySection from './DaySection';
import DayMeetings from './DayMeetings';
import DayTasks from './DayTasks';
import DayLeads from './DayLeads';
import DayUploads from './DayUploads';
import MoneyStrip from './MoneyStrip';

const isOwnedBy = (value, partnerId) => String(value) === String(partnerId);

export default function MyDay() {
  const { partner: me } = useAuth();
  const alerts = useAlerts();
  const [mine, setMine] = useState(false);

  const today = todayStr();
  // The diary for one day, plus the partners the calendar's colour rule reads. Both are
  // the calendar's own calls — the same window endpoint, asked for a range of one.
  const { data, loading, error } = useAsync(
    () => Promise.all([meetingsApi.window(today, today), authApi.partners()]),
    [today],
  );
  const [meetings, partners] = data || [[], []];

  // THE FILTER IS ONE QUESTION ASKED OF THREE LISTS, in the browser, over rows already
  // fetched — so switching between שלי and של שנינו costs nothing and cannot disagree
  // with what the other view would have shown. Each source names its own owner and is
  // asked in its own terms: a task its assignee, a follow-up its lead's owner, and a
  // meeting BOTH of its sides, because an internal meeting is owned by one partner and
  // held with the other and belongs on both their days.
  const meetingRows = mine ? meetings.filter((m) => involvesPartner(m, me.id)) : meetings;
  const taskRows = mine
    ? alerts.tasks.rows.filter((t) => isOwnedBy(t.assigned_partner_id, me.id))
    : alerts.tasks.rows;
  const leadRows = mine
    ? alerts.leads.rows.filter((l) => isOwnedBy(l.owner_partner_id, me.id))
    : alerts.leads.rows;

  return (
    <>
      <PageHeader title={he.myDay.title} spread>
        <span className="muted day-date"><DateText value={today} /></span>
        <div className="seg">
          <Button small variant={mine ? 'subtle' : ''} onClick={() => setMine(false)}>
            {he.myDay.all}
          </Button>
          <Button small variant={mine ? '' : 'subtle'} onClick={() => setMine(true)}>
            {he.myDay.mine}
          </Button>
        </div>
      </PageHeader>

      {/* EVERY SECTION, EVERY TIME — including the ones with nothing in them. A section
          that disappeared when it was empty left the page unreadable in the only state
          where it most needs to be read: with no משימות block on screen there is no
          telling "nothing is due today" from "that part did not load". So the page is
          always the same four blocks in the same order, and an empty one says which kind
          of nothing it is. The both/mine filter reaches the wording too — "אין משימות"
          and "אין לך משימות" are different facts. */}
      <AsyncBoundary loading={loading || alerts.loading} error={error}>
        <div className="day-stack">
          <MoneyStrip money={alerts.money} />

          <DaySection
            title={he.myDay.sectionMeetings}
            count={meetingRows.length}
            empty={mine ? he.myDay.noMeetingsMine : he.myDay.noMeetings}
          >
            <DayMeetings meetings={meetingRows} partners={partners} />
          </DaySection>

          <DaySection
            title={he.myDay.sectionTasks}
            count={taskRows.length}
            empty={mine ? he.myDay.noTasksMine : he.myDay.noTasks}
          >
            <DayTasks rows={taskRows} />
          </DaySection>

          {/* THE FOURTH SECTION, and the only one the both/mine filter does not touch.
              An upload belongs to nobody here — a content writer sent it — so there is
              no owner to filter it by, and hiding it under "שלי" would hide the one
              thing on this page that arrived from outside. See DayUploads. */}
          <DaySection
            title={he.workFiles.dayTitle}
            count={alerts.uploads.rows.length}
            empty={he.workFiles.dayEmpty}
          >
            <DayUploads rows={alerts.uploads.rows} />
          </DaySection>

          <DaySection
            title={he.myDay.sectionLeads}
            count={leadRows.length}
            empty={mine ? he.myDay.noLeadsMine : he.myDay.noLeads}
          >
            <DayLeads rows={leadRows} />
          </DaySection>
        </div>
      </AsyncBoundary>
    </>
  );
}
